/**
 * TeamMemberCard - Displays a team member with presence and workload (DRR-061)
 *
 * Data-driven: All data from backend getTeamWorkspace query.
 * No hardcoded roles, statuses, or actor types.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Uses backend enum values for status display
 * - No client-side ID generation
 */

import {
  Avatar,
  Badge,
  Card,
  Group,
  Progress,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';

/** Presence status from backend (DRR-061) */
export type PresenceStatus = 'ONLINE' | 'AWAY' | 'OFFLINE';

/** Props driven by backend data */
export interface TeamMemberCardProps {
  name: string;
  actorId: string;
  actorType?: string;
  role?: string;
  presenceStatus: PresenceStatus;
  currentTaskName?: string | null;
  workload: number;
  maxCapacity: number;
  successRate?: number | null;
}

const PRESENCE_COLORS: Record<PresenceStatus, string> = {
  ONLINE: 'green',
  AWAY: 'yellow',
  OFFLINE: 'gray',
};

const PRESENCE_LABELS: Record<PresenceStatus, string> = {
  ONLINE: 'Online',
  AWAY: 'Away',
  OFFLINE: 'Offline',
};

/** Get initials from a name for avatar fallback */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * TeamMemberCard - Shows a single team member with status and workload.
 *
 * Used in TeamWorkspacePage grid and Dashboard team summary.
 */
export function TeamMemberCard({
  name,
  role,
  presenceStatus,
  currentTaskName,
  workload,
  maxCapacity,
  successRate,
}: TeamMemberCardProps) {
  const utilization = maxCapacity > 0 ? (workload / maxCapacity) * 100 : 0;
  const utilizationColor =
    utilization > 80 ? 'red' : utilization > 50 ? 'yellow' : 'teal';

  return (
    <Card withBorder padding="sm" radius="md">
      <Group gap="sm" wrap="nowrap">
        <Avatar size="md" radius="xl" color="blue">
          {getInitials(name)}
        </Avatar>
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="nowrap">
            <Text size="sm" fw={600} truncate>
              {name}
            </Text>
            <Badge
              size="xs"
              variant="dot"
              color={PRESENCE_COLORS[presenceStatus]}
            >
              {PRESENCE_LABELS[presenceStatus]}
            </Badge>
          </Group>
          {role && (
            <Text size="xs" c="dimmed" truncate>
              {role}
            </Text>
          )}
        </Stack>
      </Group>

      {/* Current task */}
      <Text size="xs" c="dimmed" mt="xs" truncate>
        {currentTaskName ? `Working on: ${currentTaskName}` : 'No active task'}
      </Text>

      {/* Workload bar */}
      <Tooltip
        label={`${workload}/${maxCapacity} tasks (${Math.round(utilization)}%)`}
      >
        <Progress
          value={utilization}
          color={utilizationColor}
          size="sm"
          mt="xs"
        />
      </Tooltip>

      {/* Success rate */}
      {successRate != null && (
        <Text size="xs" c="dimmed" mt={4} ta="right">
          {Math.round(successRate * 100)}% success
        </Text>
      )}
    </Card>
  );
}
